import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import Backend from "i18next-http-backend";

// Australian translations that are bundled to the main bundle.
import enAUCommon from "../locales/en-AU/common.json";
import enAUHome from "../locales/en-AU/home.json";
import enAUMyAccount from "../locales/en-AU/myAccount.json";
import enAUQuiz from "../locales/en-AU/quiz.json";

// British translations that are bundled to the main bundle.
import enGBCommon from "../locales/en-GB/common.json";
import enGBHome from "../locales/en-GB/home.json";
import enGBMyAccount from "../locales/en-GB/myAccount.json";
import enGBQuiz from "../locales/en-GB/quiz.json";

// English translations that are bundled to the main bundle.
import enCommon from "../locales/en/common.json";
import enHome from "../locales/en/home.json";
import enMyAccount from "../locales/en/myAccount.json";
import enMyBox from "../locales/en/myBox.json";
import enQuiz from "../locales/en/quiz.json";
import enTopNavbar from "../locales/en/topNavbar.json";
import enMyTasteProfile from "../locales/en/myTasteProfile.json";
import enInStore from "../locales/en/inStore.json";
import enWin from "../locales/en/win.json";
import enBeerQuiz from "../locales/en/beerQuiz.json";
import { I18N_NAMESPACES } from "./constants";
import { fallbackAwareReadiness } from "./i18nFallbackReadiness";
import { getCountry, COUNTRY_INFO_BY_CODE, AU_COUNTRY_CODE } from "./urls.helper";

// if countrycode isn't detected in the URL, default to en_AU.
// When we have the new routes here, there should be a redirect here to au if nothing else is present.
const defaultCountryObject = COUNTRY_INFO_BY_CODE[AU_COUNTRY_CODE];
const countryFromURL = getCountry();
const urlLanguageObject = COUNTRY_INFO_BY_CODE[countryFromURL];

const { language } = urlLanguageObject || defaultCountryObject;

i18n

  // i18next-http-backend
  // loads translations from your server
  // https://github.com/i18next/i18next-http-backend
  .use(Backend)

  // pass the i18n instance to react-i18next.
  .use(initReactI18next)

  // Regional overrides usually land before the English fallback that holds most keys.
  // Keep a namespace suspended until both have finished, so pages never render in between.
  .use(fallbackAwareReadiness)

  // init i18next
  // for all options read: https://www.i18next.com/overview/configuration-options
  .init({
    fallbackLng: "en",
    lng: language,
    // Refresh mounted translations whenever more resources finish loading, e.g. copy read
    // from a namespace that another component loaded later.
    react: { bindI18n: "languageChanged loaded" },

    // ns are the Namespaces that we want loaded by default every time we import i18n.
    // Keep this list to namespaces actually used during the first paint of Layout +
    // any landing page; everything else loads on-demand via useTranslation in the
    // component that needs it. Adding a namespace here that isn't bundled in 'en'
    // (or whatever locale ends up active) will fire an http-backend fetch before
    // FCP, so resist the urge to "just add it in case."
    ns: [I18N_NAMESPACES.MY_ACCOUNT, I18N_NAMESPACES.TOP_NAVBAR],
  });

export default i18n;

/**
 * Returns cash amount with two digits if it's a float.
 * @param {number | null | undefined} amount
 * @returns {string}
 */
export const getCashAmountLabel = (amount) => {
  // const isCashCostFloat = isFloat(amount ?? 0);
  const isNegativeAmount = (amount ?? 0) < 0;
  const translationKey = isNegativeAmount ? "common:negativeCurrency" : "common:currency";
  const cashString = i18n.t(translationKey, {
    amount: Math.abs(amount),
    // TODO: Investigate need for more decimals at a later date.
    // context: isCashCostFloat && CURRENCY_FLOAT_CONTEXT,

    // The specified fallbackLng is set in order for the tests to run.
    fallbackLng: "en-AU",
  });
  return cashString;
};

// The translations below are added to the main bundle to enable faster loading of the home page.
// These are the translations needed for non-logged in users that land on the home page.
i18n.addResourceBundle("en", I18N_NAMESPACES.COMMON, enCommon);
i18n.addResourceBundle("en", I18N_NAMESPACES.HOME, enHome);
i18n.addResourceBundle("en", I18N_NAMESPACES.MY_ACCOUNT, enMyAccount);
i18n.addResourceBundle("en", I18N_NAMESPACES.MY_BOX, enMyBox);
i18n.addResourceBundle("en", I18N_NAMESPACES.QUIZ, enQuiz);
i18n.addResourceBundle("en", I18N_NAMESPACES.TOP_NAVBAR, enTopNavbar);
i18n.addResourceBundle("en", I18N_NAMESPACES.MY_TASTE_PROFILE, enMyTasteProfile);
i18n.addResourceBundle("en", I18N_NAMESPACES.IN_STORE, enInStore);
i18n.addResourceBundle("en", I18N_NAMESPACES.WIN, enWin);
i18n.addResourceBundle("en", I18N_NAMESPACES.BEER_QUIZ, enBeerQuiz);

i18n.addResourceBundle("en-AU", I18N_NAMESPACES.COMMON, enAUCommon);
i18n.addResourceBundle("en-AU", I18N_NAMESPACES.HOME, enAUHome);
i18n.addResourceBundle("en-AU", I18N_NAMESPACES.MY_ACCOUNT, enAUMyAccount);
i18n.addResourceBundle("en-AU", I18N_NAMESPACES.QUIZ, enAUQuiz);

i18n.addResourceBundle("en-GB", I18N_NAMESPACES.COMMON, enGBCommon);
i18n.addResourceBundle("en-GB", I18N_NAMESPACES.HOME, enGBHome);
i18n.addResourceBundle("en-GB", I18N_NAMESPACES.MY_ACCOUNT, enGBMyAccount);
i18n.addResourceBundle("en-GB", I18N_NAMESPACES.QUIZ, enGBQuiz);

/**
 * Translation contract — read this before adding/removing locale files.
 *
 * - `en` holds non-localised content (everything that's the same for every region).
 * - `en-AU` and `en-GB` hold *only* the keys that need region-specific phrasing
 *   (currency, store names, legal text). A localised key appears in BOTH `en-AU`
 *   AND `en-GB` (paired override sets) — never just one — except for namespaces
 *   listed in REGION_ONLY_NAMESPACES below.
 *
 * The lists below tell i18next-http-backend not to fetch JSON files that don't
 * exist on disk. Without these, every `useTranslation('myBox')` on an en-AU user
 * fires a 404 round-trip (HTML SPA fallback served as JSON, silently parse-fails,
 * falls back to en) — and these fetches block FCP. See `home-perf-followups.md`
 * Phase 1 for the trace.
 *
 * MAINTENANCE: i18n contract tests in `src/helpers/i18n.test.js` enforce this.
 * If a test fails after you change a locale file, the failure message will tell
 * you what action to take (add an import, remove a suppression, etc.).
 */

// Namespaces deliberately scoped to a single region. Skip the paired-override
// requirement for these.
//   IN_STORE: only one physical store exists (Sydney) — content is AU-only by
//     design. en bundle currently holds the strings; moving them into en-AU is
//     a future cleanup but not blocking.
//   MY_ORDERS / MY_PROGRESS / MY_TASTE_PROFILE: en-GB overrides exist purely to
//     suffix the page <title> with " UK" (en defaults to plain "Good Pair Days"
//     branding which AU happily uses). No paired AU override needed.
const REGION_ONLY_NAMESPACES = {
  [I18N_NAMESPACES.IN_STORE]: ["en-AU"],
  [I18N_NAMESPACES.MY_ORDERS]: ["en-GB"],
  [I18N_NAMESPACES.MY_PROGRESS]: ["en-GB"],
  [I18N_NAMESPACES.MY_TASTE_PROFILE]: ["en-GB"],
};

// Namespaces bundled in `en` but with no AU/GB override files on disk.
// Registered as empty bundles to short-circuit the http-backend fetch.
const NAMESPACES_WITH_NO_AU_OVERRIDE = [
  I18N_NAMESPACES.TOP_NAVBAR,
  I18N_NAMESPACES.MY_BOX,
  I18N_NAMESPACES.WIN,
  I18N_NAMESPACES.BEER_QUIZ,
  I18N_NAMESPACES.IN_STORE,
  I18N_NAMESPACES.MY_TASTE_PROFILE,
];
const NAMESPACES_WITH_NO_GB_OVERRIDE = [
  I18N_NAMESPACES.TOP_NAVBAR,
  I18N_NAMESPACES.MY_BOX,
  I18N_NAMESPACES.WIN,
  I18N_NAMESPACES.BEER_QUIZ,
  I18N_NAMESPACES.IN_STORE,
];

NAMESPACES_WITH_NO_AU_OVERRIDE.forEach((ns) => i18n.addResourceBundle("en-AU", ns, {}));
NAMESPACES_WITH_NO_GB_OVERRIDE.forEach((ns) => i18n.addResourceBundle("en-GB", ns, {}));

// Exposed for the contract tests in `src/helpers/i18n.test.js`. Not for runtime use.
export const i18nContract = {
  bundledEnNamespaces: [
    I18N_NAMESPACES.COMMON,
    I18N_NAMESPACES.HOME,
    I18N_NAMESPACES.MY_ACCOUNT,
    I18N_NAMESPACES.MY_BOX,
    I18N_NAMESPACES.QUIZ,
    I18N_NAMESPACES.TOP_NAVBAR,
    I18N_NAMESPACES.MY_TASTE_PROFILE,
    I18N_NAMESPACES.IN_STORE,
    I18N_NAMESPACES.WIN,
    I18N_NAMESPACES.BEER_QUIZ,
  ],
  bundledAuNamespaces: [
    I18N_NAMESPACES.COMMON,
    I18N_NAMESPACES.HOME,
    I18N_NAMESPACES.MY_ACCOUNT,
    I18N_NAMESPACES.QUIZ,
  ],
  bundledGbNamespaces: [
    I18N_NAMESPACES.COMMON,
    I18N_NAMESPACES.HOME,
    I18N_NAMESPACES.MY_ACCOUNT,
    I18N_NAMESPACES.QUIZ,
  ],
  suppressedAuFetches: NAMESPACES_WITH_NO_AU_OVERRIDE,
  suppressedGbFetches: NAMESPACES_WITH_NO_GB_OVERRIDE,
  regionOnlyNamespaces: REGION_ONLY_NAMESPACES,
  initNamespaces: [I18N_NAMESPACES.MY_ACCOUNT, I18N_NAMESPACES.TOP_NAVBAR],
};
